import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { useFeatureEntitlements } from "@frontegg/react";
import { Accordion, Chip } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { formatCashFull } from "utils/formatCash";
import useListMerchant from "hooks/useListMerchant";
import useListMerchantCategories from "hooks/useListMerchantCategories";
import CardLimitRow from "components/common/cards/CardLimitRow";
import RenderCardSettingPolicy from "components/common/cards/RenderCardSettingPolicy";
import { sameByIds, type CardSpendingSummary } from "utils/cards/cardRowData";
import type PolicyRequired from "enums/PolicyRequired";

// id is non-nullable to match what useListMerchant accepts; the queries select it.
interface Named {
  id?: string;
  name?: string | null;
}

export interface CardSpendPolicy {
  transaction_limit?: number | null;
  daily_limit?: number | null;
  weekly_limit?: number | null;
  monthly_limit?: number | null;
  total_limit?: number | null;
  allocation_policy?: PolicyRequired | null;
  receipt_policy?: PolicyRequired | null;
  merchants?: Named[] | null;
  blocked_merchants?: Named[] | null;
  allowed_categories?: Named[] | null;
  blocked_categories?: Named[] | null;
  expenseCategoryData?: Named | null;
  allocationData?: (Named & { allocationCode?: string | null }) | null;
}

interface CardSpendControlsProps {
  policy?: CardSpendPolicy | null;
  pendingPolicy?: CardSpendPolicy | null;
  spendingSummary?: CardSpendingSummary | null;
  /** A request is changing this card, so pending values are worth drawing. */
  hasPendingChange?: boolean;
}

type LimitRow = {
  key: string;
  label: string;
  value: React.ReactNode;
  pendingValue?: React.ReactNode;
  spentPercent?: number;
};

/** A request that adds or clears an allocation leaves the other side empty. */
const allocationLabel = (
  allocation: { name?: string | null; allocationCode?: string | null } | null,
  none: string,
) =>
  allocation?.name
    ? `${allocation.name} : ${allocation.allocationCode ?? ""}`
    : none;

const headingTint = (changed?: boolean) => ({
  color: changed ? "var(--color-text-brand)" : "inherit",
});

export const CardSpendControlsTab: React.FC<CardSpendControlsProps> = ({
  policy,
  pendingPolicy,
  spendingSummary,
  hasPendingChange = false,
}) => {
  const { t } = useTranslation();
  const { isEntitled: featureCardTypes } =
    useFeatureEntitlements("feature_card_types");
  const { isEntitled: featureMerchantLimits } = useFeatureEntitlements(
    "feature_merchant_limits",
  );
  const { isEntitled: featureAllocations } = useFeatureEntitlements(
    "feature_allocations",
  );

  const listMerchant = useListMerchant(policy, t);
  const listPendingMerchant = useListMerchant(pendingPolicy, t);
  const listBlockedMerchant = useListMerchant(
    { merchants: policy?.blocked_merchants },
    t,
  );
  const listPendingBlockedMerchant = useListMerchant(
    { merchants: pendingPolicy?.blocked_merchants },
    t,
  );
  const listMerchantCategory = useListMerchantCategories(policy, t);
  const listPendingMerchantCategory = useListMerchantCategories(
    pendingPolicy,
    t,
  );
  const listBlockedCategory = useListMerchantCategories(
    { allowed_categories: policy?.blocked_categories },
    t,
  );
  const listPendingBlockedCategory = useListMerchantCategories(
    { allowed_categories: pendingPolicy?.blocked_categories },
    t,
  );

  const amountAgainstLimit = (limit?: number | null, spent?: number) =>
    !limit
      ? t("No limit")
      : spent !== undefined
        ? t("AmountOfLimit", {
            spent: formatCashFull(Math.abs(spent)),
            limit: formatCashFull(limit),
          })
        : formatCashFull(limit);

  const limitRow = (
    key: string,
    label: string,
    current?: number | null,
    pending?: number | null,
    spent?: number,
  ): LimitRow[] => {
    if (!current && !(hasPendingChange && pending)) return [];
    return [
      {
        key,
        label,
        value: amountAgainstLimit(current, spent),
        pendingValue:
          hasPendingChange && pending !== current
            ? amountAgainstLimit(pending, spent)
            : undefined,
        spentPercent:
          current && spent !== undefined
            ? Math.min(100, (Math.abs(spent) / Math.abs(current)) * 100)
            : undefined,
      },
    ];
  };

  const chips = (names: string[]) => (
    <Inline gap="var(--space-2)" justify="end">
      {names.map((name) => (
        <Chip key={name} size="sm">
          {name}
        </Chip>
      ))}
    </Inline>
  );

  const restrictionRow = (
    key: string,
    label: string,
    names: string[],
    pendingNames: string[],
    isChanged: boolean,
  ): LimitRow[] =>
    names.length === 0 && !(hasPendingChange && isChanged)
      ? []
      : [
          {
            key,
            label,
            value: chips(names),
            pendingValue:
              hasPendingChange && isChanged ? chips(pendingNames) : undefined,
          },
        ];

  const limitRows: LimitRow[] = [
    ...limitRow(
      "transaction",
      t("Transaction Limit"),
      policy?.transaction_limit,
      pendingPolicy?.transaction_limit,
    ),
    ...limitRow(
      "daily",
      t("Daily Limit"),
      policy?.daily_limit,
      pendingPolicy?.daily_limit,
      spendingSummary?.dailySpent ?? undefined,
    ),
    ...limitRow(
      "weekly",
      t("Weekly Limit"),
      policy?.weekly_limit,
      pendingPolicy?.weekly_limit,
      spendingSummary?.weeklySpent ?? undefined,
    ),
    ...limitRow(
      "monthly",
      t("Monthly Limit"),
      policy?.monthly_limit,
      pendingPolicy?.monthly_limit,
      spendingSummary?.monthlySpent ?? undefined,
    ),
    ...limitRow(
      "total",
      t("Total Limit"),
      policy?.total_limit,
      pendingPolicy?.total_limit,
      spendingSummary?.totalSpent ?? undefined,
    ),
  ];

  const restrictionRows: LimitRow[] = featureMerchantLimits
    ? [
        ...restrictionRow(
          "allowedMerchants",
          t("Allowed Merchants"),
          listMerchant,
          listPendingMerchant,
          !sameByIds(policy?.merchants, pendingPolicy?.merchants),
        ),
        ...restrictionRow(
          "blockedMerchants",
          t("Blocked Merchants"),
          listBlockedMerchant,
          listPendingBlockedMerchant,
          !sameByIds(
            policy?.blocked_merchants,
            pendingPolicy?.blocked_merchants,
          ),
        ),
        ...restrictionRow(
          "allowedCategories",
          t("Allowed Categories"),
          listMerchantCategory,
          listPendingMerchantCategory,
          !sameByIds(
            policy?.allowed_categories,
            pendingPolicy?.allowed_categories,
          ),
        ),
        ...restrictionRow(
          "blockedCategories",
          t("Blocked Categories"),
          listBlockedCategory,
          listPendingBlockedCategory,
          !sameByIds(
            policy?.blocked_categories,
            pendingPolicy?.blocked_categories,
          ),
        ),
      ]
    : [];

  // Only what is actually set; an unset card shows one line rather than two
  // rows each saying "No ...".
  const categorizationRows: LimitRow[] = [
    ...(policy?.expenseCategoryData?.id ||
    (hasPendingChange && pendingPolicy?.expenseCategoryData?.id)
      ? [
          {
            key: "expenseCategory",
            label: t("Expense Category"),
            value: policy?.expenseCategoryData?.name ?? t("No category"),
            pendingValue:
              hasPendingChange &&
              pendingPolicy?.expenseCategoryData?.id !==
                policy?.expenseCategoryData?.id
                ? (pendingPolicy?.expenseCategoryData?.name ?? t("No category"))
                : undefined,
          },
        ]
      : []),
    ...(featureAllocations &&
    (policy?.allocationData?.id ||
      (hasPendingChange && pendingPolicy?.allocationData?.id))
      ? [
          {
            key: "allocation",
            label: t("Allocation"),
            value: allocationLabel(
              policy?.allocationData ?? null,
              t("No allocation"),
            ),
            pendingValue:
              hasPendingChange &&
              pendingPolicy?.allocationData?.id !== policy?.allocationData?.id
                ? allocationLabel(
                    pendingPolicy?.allocationData ?? null,
                    t("No allocation"),
                  )
                : undefined,
          },
        ]
      : []),
  ];

  const restrictionsChanged =
    !sameByIds(policy?.merchants, pendingPolicy?.merchants) ||
    !sameByIds(policy?.blocked_merchants, pendingPolicy?.blocked_merchants) ||
    !sameByIds(policy?.allowed_categories, pendingPolicy?.allowed_categories) ||
    !sameByIds(policy?.blocked_categories, pendingPolicy?.blocked_categories);

  // Which sections the pending request actually touches, so only those tint.
  const changed = {
    spendingLimits:
      hasPendingChange &&
      (pendingPolicy?.transaction_limit !== policy?.transaction_limit ||
        pendingPolicy?.daily_limit !== policy?.daily_limit ||
        pendingPolicy?.weekly_limit !== policy?.weekly_limit ||
        pendingPolicy?.monthly_limit !== policy?.monthly_limit ||
        pendingPolicy?.total_limit !== policy?.total_limit),
    merchantControls: hasPendingChange && restrictionsChanged,
    categorizations:
      hasPendingChange &&
      (pendingPolicy?.expenseCategoryData?.id !==
        policy?.expenseCategoryData?.id ||
        pendingPolicy?.allocationData?.id !== policy?.allocationData?.id),
    policies:
      hasPendingChange &&
      (pendingPolicy?.receipt_policy !== policy?.receipt_policy ||
        pendingPolicy?.allocation_policy !== policy?.allocation_policy),
  };

  const emptyNote = (text: string) => (
    <Typography
      variant="body1"
      sx={{
        color: "var(--color-text-tertiary)",
        paddingBlock: "var(--space-3)",
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Stack gap="0">
      <Accordion
        title={
          <Box component="span" sx={headingTint(changed.spendingLimits)}>
            {t("Spending Limits")}
          </Box>
        }
        defaultExpanded={true}
      >
        {limitRows.map((row) => (
          <CardLimitRow
            key={row.key}
            label={row.label}
            value={row.value}
            pendingValue={row.pendingValue}
            spentPercent={row.spentPercent}
          />
        ))}
      </Accordion>

      {/* Where the card can be spent is a different question from how much. */}
      {featureMerchantLimits && (
        <Accordion
          title={
            <Box component="span" sx={headingTint(changed.merchantControls)}>
              {t("Merchant Controls")}
            </Box>
          }
        >
          {restrictionRows.length === 0
            ? emptyNote(t("No merchant or category restrictions on this card"))
            : restrictionRows.map((row) => (
                <CardLimitRow
                  key={row.key}
                  label={row.label}
                  value={row.value}
                  pendingValue={row.pendingValue}
                />
              ))}
        </Accordion>
      )}

      {featureCardTypes && (
        <Accordion
          title={
            <Box component="span" sx={headingTint(changed.categorizations)}>
              {t("Categorizations")}
            </Box>
          }
        >
          {categorizationRows.length === 0
            ? emptyNote(t("No categorizations set for this card"))
            : categorizationRows.map((row) => (
                <CardLimitRow
                  key={row.key}
                  label={row.label}
                  value={row.value}
                  pendingValue={row.pendingValue}
                />
              ))}
        </Accordion>
      )}

      <Accordion
        title={
          <Box component="span" sx={headingTint(changed.policies)}>
            {t("Policies")}
          </Box>
        }
      >
        {featureAllocations && (
          <CardLimitRow
            label={t("Allocation Policy")}
            value={
              !policy?.allocation_policy ? (
                t("Follow policy")
              ) : (
                <RenderCardSettingPolicy policy={policy.allocation_policy} />
              )
            }
            pendingValue={
              hasPendingChange &&
              pendingPolicy?.allocation_policy &&
              pendingPolicy.allocation_policy !== policy?.allocation_policy ? (
                <RenderCardSettingPolicy
                  policy={pendingPolicy.allocation_policy}
                />
              ) : undefined
            }
          />
        )}
        <CardLimitRow
          label={t("Receipt Policy")}
          value={
            !policy?.receipt_policy ? (
              t("Follow policy")
            ) : (
              <RenderCardSettingPolicy policy={policy.receipt_policy} />
            )
          }
          pendingValue={
            hasPendingChange &&
            pendingPolicy?.receipt_policy &&
            pendingPolicy.receipt_policy !== policy?.receipt_policy ? (
              <RenderCardSettingPolicy policy={pendingPolicy.receipt_policy} />
            ) : undefined
          }
        />
      </Accordion>
    </Stack>
  );
};

export default CardSpendControlsTab;
